"use strict";

var CONTROL_SELECTOR = "input, select, textarea";

/**
 * Collect the form controls carrying an error, in document order.
 *
 * `is-invalid` lands on wrapper elements as well as on controls (`.validate-group`,
 * the gift card button row, and the `.form-group:has(.is-invalid)` rule in SCSS), so a
 * wrapper match is resolved down to the controls inside it. jQuery's `add` returns the
 * union in document order, which is what "the first invalid field" has to mean.
 *
 * @param {jQuery|string|Element} [scope] - container to search; defaults to the document
 * @returns {jQuery} the invalid controls
 */
function invalidControls(scope) {
    var $invalid = scope
        ? $(scope).find(".is-invalid").addBack(".is-invalid")
        : $(".is-invalid");

    return $invalid.filter(CONTROL_SELECTOR).add($invalid.find(CONTROL_SELECTOR));
}

/**
 * Move keyboard focus to the first invalid control so that assistive technology
 * announces the error text its `aria-describedby` already points at. Without this,
 * the message is painted on screen and never read out (WCAG 4.1.3, 3.3.1).
 *
 * Scrolling is deliberately left to the caller's existing `scrollAnimate`/`animate`
 * call, which carries site-specific offsets for the sticky header. `preventScroll`
 * stops the browser jumping straight to the field and cutting that animation short.
 *
 * @param {jQuery|string|Element} [scope] - container to search; defaults to the document
 * @returns {jQuery} the focused control, empty when nothing is invalid
 */
function focusFirstInvalid(scope) {
    var $first = invalidControls(scope).filter(":visible").first();

    if ($first.length) {
        $first[0].focus({ preventScroll: true });
    }

    return $first;
}

/**
 * Move focus to a specific control.
 *
 * `focusFirstInvalid` searches for `is-invalid`, so it cannot reach error paths that never
 * set that class. The Givex gift card responses are one: the message reports on the card
 * number and PIN together rather than marking either field, and it renders into a shared
 * container sitting after the buttons.
 *
 * @param {jQuery|string|Element} element - the control to focus
 * @returns {jQuery} the focused control, empty when it is absent or hidden
 */
function focusControl(element) {
    var $control = $(element).filter(":visible").first();

    if ($control.length) {
        $control[0].focus({ preventScroll: true });
    }

    return $control;
}

/**
 * Flag controls as invalid for assistive technology. `is-invalid` is only a paint
 * class; on its own nothing tells a screen reader that the field is in an error state.
 *
 * @param {jQuery|string|Element} elements - controls, or wrappers containing them
 * @returns {void}
 */
function markInvalid(elements) {
    var $elements = $(elements);

    $elements.filter(CONTROL_SELECTOR)
        .add($elements.find(CONTROL_SELECTOR))
        .attr("aria-invalid", "true");
}

/**
 * Clear the invalid flag. Pair this with every `removeClass("is-invalid")` so that the
 * paint class and the ARIA state cannot drift apart. Paired up across checkout, address
 * and account/login today; the promo code and PDP gift card fields still only toggle the
 * class (see DGF-3994 notes).
 *
 * @param {jQuery|string|Element} scope - control, or container whose controls to reset
 * @returns {void}
 */
function clearInvalid(scope) {
    var $scope = $(scope);

    $scope.filter("[aria-invalid]").removeAttr("aria-invalid");
    $scope.find("[aria-invalid]").removeAttr("aria-invalid");
}

module.exports = {
    focusFirstInvalid: focusFirstInvalid,
    focusControl: focusControl,
    invalidControls: invalidControls,
    markInvalid: markInvalid,
    clearInvalid: clearInvalid
};
